import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bell, ArrowRight, CheckCircle, XCircle, Package, Clock, Truck, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  TRANSFER_CREATED: { icon: Package, color: "text-blue-600 bg-blue-100", label: "CREATED" },
  IN_TRANSIT: { icon: Truck, color: "text-amber-600 bg-amber-100", label: "IN TRANSIT" },
  TRANSFER_ACCEPTED: { icon: CheckCircle, color: "text-green-600 bg-green-100", label: "RECEIVED" },
  TRANSFER_REJECTED: { icon: XCircle, color: "text-red-600 bg-red-100", label: "REJECTED" },
  STOCK_RETURNED: { icon: Undo2, color: "text-purple-600 bg-purple-100", label: "RETURNED TO SOURCE" },
};

const fmtTime = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-PH", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
};

export const TransferNotifications = ({ branch }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [timeline, setTimeline] = useState(null); // {transfer_id, events}
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!branch) return;
    try {
      const resp = await axios.get(`${API}/notifications`, { params: { branch } });
      setNotifications(resp.data.notifications || []);
      setUnread(resp.data.unread_count || 0);
    } catch (e) {
      console.error("Notif fetch error:", e);
    }
  }, [branch]);

  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 60000);
    return () => clearInterval(t);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, { branch });
      fetchNotifications();
    } catch (e) { console.error(e); }
  };

  const openTimeline = async (notif) => {
    if (!notif.transfer_id) return;
    setLoadingTimeline(true);
    try {
      if (!notif.read) {
        axios.post(`${API}/notifications/mark-read`, { ids: [notif.id] }).then(fetchNotifications);
      }
      const resp = await axios.get(`${API}/transfers/${notif.transfer_id}/events`);
      setTimeline(resp.data);
    } catch (e) {
      console.error("Timeline fetch error:", e);
    } finally {
      setLoadingTimeline(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); fetchNotifications(); }}
        className="relative flex items-center gap-1 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
        data-testid="notifications-bell"
        title="Transfer notifications"
      >
        <Bell className="h-4 w-4 text-gray-600" />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notifications-unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Notifications panel */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="notifications-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-600" />
              Notifications — {branch}
            </DialogTitle>
            <DialogDescription>
              Lahat ng transfer actions (sent, received, rejected, returned). I-click ang notification para makita ang buong history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {notifications.length === 0 ? (
              <p className="text-center text-gray-500 py-6">Walang notifications</p>
            ) : (
              notifications.map((n, idx) => (
                <div
                  key={n.id}
                  onClick={() => openTimeline(n)}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${n.read ? "bg-white hover:bg-gray-50" : "bg-orange-50 border-orange-200 hover:bg-orange-100"}`}
                  data-testid={`notification-item-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className={`text-sm ${n.read ? "font-medium text-gray-700" : "font-bold text-gray-900"}`}>{n.title}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{n.message}</p>
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {fmtTime(n.created_at)}
                        {n.transfer_id && <span className="text-blue-500 ml-1 flex items-center gap-0.5">• View history <ArrowRight className="h-3 w-3" /></span>}
                      </p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            {unread > 0 && (
              <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">Mark all read</Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer timeline modal */}
      <Dialog open={!!timeline} onOpenChange={(v) => !v && setTimeline(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="transfer-timeline-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              Transfer History
            </DialogTitle>
            <DialogDescription>
              {timeline?.transfer_id && <span className="font-mono text-xs">#{timeline.transfer_id}</span>} — kumpletong immutable audit trail ng transfer na ito.
            </DialogDescription>
          </DialogHeader>
          {loadingTimeline ? (
            <p className="text-center text-gray-500 py-6">Loading...</p>
          ) : (
            <div className="py-2">
              {(timeline?.events || []).length === 0 ? (
                <p className="text-center text-gray-500 py-6">Walang history record</p>
              ) : (
                <div className="relative pl-6 space-y-5 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-200">
                  {timeline.events.map((ev, idx) => {
                    const meta = ACTION_META[ev.action] || { icon: Package, color: "text-gray-600 bg-gray-100", label: ev.action };
                    const Icon = meta.icon;
                    return (
                      <div key={ev.id || idx} className="relative" data-testid={`timeline-event-${idx}`}>
                        <span className={`absolute -left-6 top-0 w-5 h-5 rounded-full flex items-center justify-center ${meta.color}`}>
                          <Icon className="h-3 w-3" />
                        </span>
                        <p className="text-xs text-gray-400">{fmtTime(ev.timestamp)}</p>
                        <p className="text-sm font-bold text-gray-800">
                          {meta.label}
                          <span className="font-normal text-gray-500 ml-2">{ev.from_branch} → {ev.to_branch}</span>
                        </p>
                        <p className="text-sm text-gray-600">{ev.quantity} x {ev.item_name}</p>
                        {ev.actor && ev.actor !== "system" && <p className="text-xs text-gray-500">Ni: {ev.actor} ({ev.acting_branch})</p>}
                        {ev.reason && <p className="text-xs text-red-600 mt-0.5">"{ev.reason}"</p>}
                        {ev.prev_status && ev.prev_status !== "-" && (
                          <p className="text-[10px] text-gray-400">Status: {ev.prev_status} → {ev.new_status}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeline(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
